import React from 'react';
import { Link } from 'react-router-dom';
import { PRO_MONTHLY_USD } from '../config/plans';

const TeamPortalSection = () => {
  return (
    <section className="border-t border-slate-200/80 bg-gradient-to-br from-slate-900 via-slate-900 to-cyan-950 py-16 text-white md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <span className="inline-flex items-center rounded-full bg-cyan-400/15 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-cyan-200 ring-1 ring-cyan-400/25">
              Pro feature
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight md:text-4xl">
              Give your crew a secure team portal — without sharing your login.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-300 md:text-lg">
              Invite up to two team members on Pro. They get their own sign-in, see only the projects you assign, and
              can upload photos, notes, and expenses based on the permissions you set per job.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-slate-200 md:text-base">
              <li className="flex gap-3">
                <span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500/20 text-cyan-300">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
                <span>Generate one-time invite links from Teams → Portal Members</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500/20 text-cyan-300">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
                <span>Control project access and section permissions per team member</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-500/20 text-cyan-300">
                  <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
                <span>Separate team login at devellotech.ca — works on phone and desktop</span>
              </li>
            </ul>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/plans" className="inline-flex">
                <button
                  type="button"
                  className="w-full rounded-2xl bg-cyan-400 px-6 py-3 text-sm font-bold text-slate-900 shadow-lg shadow-cyan-500/20 transition hover:bg-cyan-300 sm:w-auto"
                >
                  See Pro pricing — ${PRO_MONTHLY_USD}/mo
                </button>
              </Link>
              <Link to="/account?signup=1&plan=pro" className="inline-flex">
                <button
                  type="button"
                  className="w-full rounded-2xl border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition hover:bg-white/10 sm:w-auto"
                >
                  Start with Pro
                </button>
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="pointer-events-none absolute -right-6 -top-6 h-32 w-32 rounded-full bg-cyan-500/20 blur-3xl" />
            <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/5 p-6 shadow-2xl shadow-black/30 backdrop-blur-sm md:p-8">
              <div className="flex items-center justify-between border-b border-white/10 pb-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-cyan-200/80">Team portal</p>
                  <p className="mt-1 text-lg font-bold text-white">Project access you control</p>
                </div>
                <span className="rounded-full bg-emerald-400/15 px-3 py-1 text-xs font-semibold text-emerald-200 ring-1 ring-emerald-400/25">
                  2 seats included
                </span>
              </div>
              <div className="mt-5 space-y-3">
                {[
                  { name: 'Alex M.', role: 'Site lead', access: 'Photos, notes, expenses', status: 'Active' },
                  { name: 'Jordan K.', role: 'Foreman', access: 'Daily logs, change orders', status: 'Active' },
                ].map((member) => (
                  <div
                    key={member.name}
                    className="flex items-center justify-between gap-3 rounded-2xl border border-white/10 bg-slate-950/40 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold text-white">{member.name}</p>
                      <p className="truncate text-xs text-slate-400">
                        {member.role} · {member.access}
                      </p>
                    </div>
                    <span className="shrink-0 rounded-full bg-emerald-500/15 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">
                      {member.status}
                    </span>
                  </div>
                ))}
              </div>
              <div className="mt-5 rounded-2xl border border-cyan-400/20 bg-cyan-400/10 px-4 py-3 text-sm text-cyan-50">
                Open any project → <strong>Team Access</strong> to choose exactly what each member can view or edit.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TeamPortalSection;
